import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Flame, Check, Shield, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Paywall() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If user is already paid, redirect
  React.useEffect(() => {
    if (user?.is_paid) {
      navigate('/app');
    }
  }, [user, navigate]);

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      const { data } = await axios.post(`${API}/payment/checkout`, {
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe checkout
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background image with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1773303221310-a491631e0d53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjB0ZXh0dXJlJTIwcmVkJTIwbmVvbnxlbnwwfHx8fDE3NzYxODg0ODl8MA&ixlib=rb-4.1.0&q=85)'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/90 to-transparent" />
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center px-6 py-12">
        <div className="max-w-md mx-auto text-center">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <Flame className="w-10 h-10 text-rose-500" />
            <span className="font-outfit text-3xl font-bold text-white">LocalFlame</span>
          </div>

          {/* Hero text */}
          <h1 className="font-outfit text-4xl sm:text-5xl font-medium text-white tracking-tight mb-4">
            Unlock All Features
          </h1>
          
          <p className="text-zinc-400 text-lg mb-8">
            One payment. Lifetime access. No subscriptions.
          </p>

          {/* Price */}
          <div className="mb-8">
            <div className="inline-flex items-baseline gap-2">
              <span className="text-5xl font-outfit font-bold text-white">$19.99</span>
              <span className="text-zinc-500 text-lg">once</span>
            </div>
            <div className="flex items-center justify-center gap-2 mt-2 text-green-500">
              <Shield className="w-4 h-4" />
              <span className="text-sm">No hidden fees. Ever.</span>
            </div>
          </div>

          {/* Features */}
          <div className="bg-zinc-900/50 rounded-2xl p-6 mb-8 text-left border border-zinc-800/50">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-rose-500" />
              <span className="font-outfit font-semibold text-white">Everything Included</span>
            </div>
            <ul className="space-y-3">
              {[
                'Unlimited swipes',
                'See who likes you',
                'Message all matches',
                'Video calling',
                'Local matches only (25mi)',
                'No ads',
                'Priority support'
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-zinc-300">
                  <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {error && (
            <div data-testid="payment-error" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* CTA */}
          <Button
            data-testid="unlock-btn"
            onClick={handlePayment}
            disabled={loading}
            size="lg"
            className="w-full h-14 bg-rose-600 hover:bg-rose-500 text-white font-semibold text-lg rounded-full shadow-[0_8px_32px_rgba(225,29,72,0.4)] match-glow"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Unlock LocalFlame — $19.99'
            )}
          </Button>

          <p className="text-zinc-600 text-sm mt-4">
            Secure payment powered by Stripe
          </p>

          {/* Referral alternative */}
          <div className="mt-8 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50 text-center">
            <p className="text-zinc-400 text-sm">
              Don't want to pay? <span className="text-rose-400 font-medium">Invite 3 friends</span> who sign up and pay, and you get free access!
            </p>
            <p className="text-zinc-600 text-xs mt-1">
              Check your referral code on your Profile page
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
